import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';
import { Icons } from '../../../../shared/helpers/icons';
import { IContact } from '../../../../shared/models/contact';
import { DateUtils } from '../../../../shared/helpers/dateUtils';
import { UserDataService } from '../../../../shared/services/userData.service';
import { ContactsService } from '../../../../shared/services/contact.service';
import { ICprInstructor } from '../../../../shared/models/cprFirstAidAed';
import { CprFirstAidAedService } from '../../../../shared/services/cprFirstAidAed.service';

@Component({
  selector: 'cpr-instructor-form',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, SpinnerComponent],
  templateUrl: './cpr-instructor-form.component.html'
})
export class CprInstructorFormComponent implements OnInit, AfterViewInit {

  icons = Icons;

  @Input() itemToEdit: ICprInstructor | null = null;

  @Output() onClose = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<{ type: string, message: string }>();

  @ViewChild('expirationInput') expirationInput!: ElementRef;

  currentBuildingId: string | null = null;
  entry: ICprInstructor = {};
  contactsList: IContact[] = [];
  loading: boolean = false;

  constructor(
    private userDataService: UserDataService,
    private contactsService: ContactsService,
    private cprService: CprFirstAidAedService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.entry = this.itemToEdit ? { ...this.itemToEdit } : {};
    this.loadContacts();
  }

  async ngAfterViewInit(): Promise<void> {
    const { Datepicker } = await import('flowbite-datepicker');
    if (this.expirationInput) {
      new Datepicker(this.expirationInput.nativeElement, { autohide: true, format: 'mm/dd/yyyy' });
      if (this.entry.expiration_dates) {
        this.expirationInput.nativeElement.value = DateUtils.formatDateForDisplay(this.entry.expiration_dates);
      }
      this.expirationInput.nativeElement.addEventListener('changeDate', (event: any) => {
        this.entry.expiration_dates = DateUtils.formatDateForBackend(event.target.value);
      });
    }
  }

  loadContacts(): void {
    if (!this.currentBuildingId) return;
    this.contactsService.getContacts(this.currentBuildingId).subscribe({
      next: (response) => {
        this.contactsList = (response.data || []).sort((a, b) => a.name.localeCompare(b.name));
      }
    });
  }

  get isEdit(): boolean {
    return !!this.itemToEdit?.id;
  }

  get isValid(): boolean {
    return !!this.entry.name;
  }

  closeModal(): void {
    this.onClose.emit();
  }

  save(): void {
    if (!this.currentBuildingId || !this.isValid) return;

    const payload: Partial<ICprInstructor> = {
      name: this.entry.name,
      position_location: this.entry.position_location,
      certification_type: this.entry.certification_type,
      expiration_dates: this.entry.expiration_dates,
    };

    this.loading = true;
    const request$ = this.isEdit
      ? this.cprService.updateInstructor(this.currentBuildingId, String(this.itemToEdit!.id), payload)
      : this.cprService.createInstructor(this.currentBuildingId, payload);

    request$.subscribe({
      next: (response) => {
        this.loading = false;
        if (!response.has_error) {
          this.onSave.emit({ type: 'success', message: this.isEdit ? 'This occurred after updating an instructor.' : 'This occurred after adding an instructor.' });
        } else {
          this.onSave.emit({ type: 'unsuccess', message: response.message });
        }
      },
      error: (err) => {
        this.loading = false;
        this.onSave.emit({ type: 'unsuccess', message: err.message });
      }
    });
  }
}
